import {
  activityFilterFromQuery,
  activityFilterGroupSchema,
} from "@gtm/contracts/core/activity-filter";
import type { RecordFilterGroup } from "@gtm/contracts/core/record-filter";
import { useQuery } from "@tanstack/react-query";
import { RecordFilterBuilder } from "@/components/workspace/record-filter-builder";
import { reviewStatusOptions } from "@/components/workspace/review-status";
import { SourceIcon } from "@/components/workspace/source-icon";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";

export function ActivityFilterBuilder({
  query,
  repositories,
  types,
  optionsLoading,
  optionsError,
  onRetry,
  onApply,
}: {
  query: Record<string, string | undefined>;
  repositories: Array<{ id: string; name: string; provider: string }>;
  types: string[];
  optionsLoading: boolean;
  optionsError: boolean;
  onRetry?: () => void;
  onApply: (values: Record<string, string>) => void;
}) {
  const users = useQuery({
    queryKey: ["assignees"],
    queryFn: ({ signal }) => unwrap(apiClient().GET("/organization/assignees", { signal })),
    retry: false,
  });
  let applied: RecordFilterGroup = { operator: "and", rules: [] };
  let invalid = false;
  try {
    applied = activityFilterFromQuery(query);
  } catch {
    invalid = true;
  }
  return (
    <RecordFilterBuilder
      kind="activity"
      applied={applied}
      invalid={invalid}
      fields={[
        { value: "status", label: "Status" },
        { value: "type", label: "Activity" },
        { value: "source", label: "Source" },
        { value: "assignee", label: "Assignee" },
        { value: "date", label: "Date" },
        { value: "text", label: "Text" },
      ]}
      choices={{
        status: reviewStatusOptions,
        source: repositories.map((source) => ({
          value: source.id,
          label: sourceLabel(source.provider, source.name),
          icon: <SourceIcon source={source.provider} size={16} />,
        })),
        type: [...new Set(["star", "issue", "comment", "fork", "watcher", ...types])].map(
          (value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }),
        ),
        assignee: [
          { value: "unassigned", label: "Unassigned" },
          ...(users.data ?? []).map((user) => ({ value: user.id, label: user.name })),
        ],
      }}
      optionsLoading={optionsLoading || users.isPending}
      optionsError={optionsError || !!users.error}
      onRetry={() => {
        onRetry?.();
        void users.refetch();
      }}
      validate={(group) => activityFilterGroupSchema.safeParse(group).success}
      onApply={(group) =>
        onApply({
          filter: group.rules.length ? JSON.stringify(activityFilterGroupSchema.parse(group)) : "",
          review: "",
          type: "",
          repository: "",
          days: "",
          time: "",
          assignee: "",
        })
      }
    />
  );
}
